import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  GridLegacy as Grid,
  IconButton,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { App_sesion_token_key, FormatPrice } from '../../../utils/constants';
import { auctionApi, newIdempotencyKey } from '../api/auctionApi';
import { isSubastaLiveEnabled } from '../flags';
import { useAuctionSocket } from '../hooks/useAuctionSocket';
import { AuctionCountdown } from '../components/AuctionCountdown';
import { AuctionRanking } from '../components/AuctionRanking';
import { QuickBidBar } from '../components/QuickBidBar';
import { AcceptBidDialog } from '../components/AcceptBidDialog';
import { AuctionLiveStatus } from '../components/AuctionLiveStatus';

const CONFLICT_COPY = {
  BID_NOT_LOWER: 'La oferta debe ser menor al precio vigente.',
  BID_STEP_TOO_SMALL: 'El decremento mínimo no se cumple.',
  STALE_VERSION: 'El precio cambió. Revisa el vigente e inténtalo de nuevo.',
  AUCTION_CLOSED: 'La subasta ya no está abierta.',
  ALREADY_AWARDED: 'Esta subasta ya fue adjudicada.',
  PRICE_CHANGED: 'El precio vigente cambió. No se envió la oferta anterior.',
  NOT_BEST_BID: 'Solo se puede aceptar la oferta más baja.',
  FORBIDDEN: 'No tienes permiso para esta acción.',
  INVALID_BID_AMOUNT: 'El valor ofertado debe ser un número mayor que cero.',
};

export const SalaSubastaPage = ({ backTo = '/plataforma/mis-subastas' }) => {
  const { cod } = useParams();
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmBid, setConfirmBid] = useState(null);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [clientAt, setClientAt] = useState(Date.now());
  const acceptOpenRef = useRef(false);
  acceptOpenRef.current = acceptOpen;

  const token = typeof localStorage !== 'undefined' ? localStorage.getItem(App_sesion_token_key) : null;
  const enabled = isSubastaLiveEnabled();

  const loadSnapshot = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const data = await auctionApi.getSnapshot(cod);
      setSnapshot(data.value);
      setClientAt(Date.now());
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.msg || err?.msg || 'No se pudo cargar la sala');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [cod]);

  useEffect(() => {
    if (!enabled) {
      setError('La sala en vivo no está habilitada.');
      setLoading(false);
      return;
    }
    loadSnapshot(false);
  }, [enabled, loadSnapshot]);

  const onSocketEvent = useCallback((eventName, payload) => {
    if (payload?.cod_subasta && String(payload.cod_subasta) !== String(cod)) {
      return;
    }
    if (eventName === 'auction.price_updated' || eventName === 'auction.bid_created') {
      if (acceptOpenRef.current) {
        setToast({
          severity: 'warning',
          message: `El vigente ahora es $ ${FormatPrice(payload.precio_vigente)}. El dialog se actualizó.`,
        });
      }
    }
    if (eventName === 'auction.awarded') {
      setAcceptOpen(false);
      setToast((prev) => {
        if (payload.ganador_self) {
          return { severity: 'success', message: 'Ganaste esta subasta', ganadorSelf: true };
        }
        if (prev?.ganadorSelf) {
          return prev;
        }
        const nombre = payload.proveedor_ganador && payload.proveedor_ganador !== 'Otro'
          ? payload.proveedor_ganador
          : null;
        return {
          severity: 'success',
          message: nombre ? `Adjudicada a ${nombre}` : 'La subasta fue adjudicada',
        };
      });
    }
    loadSnapshot(true);
  }, [cod, loadSnapshot]);

  const { socketDown } = useAuctionSocket({
    cod,
    token,
    enabled: enabled && !!cod,
    onEvent: onSocketEvent,
  });

  useEffect(() => {
    if (!socketDown || !enabled || !snapshot || snapshot.ind_sala !== 'OPEN') {
      return undefined;
    }
    const id = setInterval(() => loadSnapshot(true), 3000);
    return () => clearInterval(id);
  }, [socketDown, enabled, snapshot?.ind_sala, loadSnapshot]);

  const salaStatus = snapshot?.ind_sala || 'OPEN';
  const isBuyer = snapshot?.rol_vista === 'buyer';
  const isOpen = salaStatus === 'OPEN';
  const best = snapshot?.ranking?.[0];
  const selfLead = snapshot?.self_puesto === 1;

  const statusMessage = useMemo(() => {
    if (salaStatus === 'AWARDED') {
      const name = isBuyer ? (best?.proveedor || 'un proveedor') : (selfLead ? 'ti' : 'otro oferente');
      return isBuyer
        ? `Adjudicada a ${name} por $ ${FormatPrice(snapshot.precio_vigente)}.`
        : (selfLead ? `Ganaste · $ ${FormatPrice(snapshot.precio_vigente)}` : 'Cerrada · no adjudicada a ti');
    }
    if (salaStatus === 'EXPIRED') {
      return 'El tiempo se agotó. No hay adjudicación automática.';
    }
    return null;
  }, [salaStatus, isBuyer, best, selfLead, snapshot]);

  const submitBid = async (valor) => {
    if (!snapshot || !isOpen) return;
    if (valor >= Number(snapshot.precio_vigente)) {
      setToast({ severity: 'error', message: CONFLICT_COPY.BID_NOT_LOWER });
      return;
    }
    const confirmed = sessionStorage.getItem('subasta_bid_confirmed') === '1';
    if (!confirmed) {
      setConfirmBid(valor);
      return;
    }
    await sendBid(valor);
  };

  const sendBid = async (valor) => {
    setSending(true);
    try {
      const data = await auctionApi.ofertar({
        cod_subasta: Number(cod),
        valor_ofertado: valor,
        version: snapshot.version,
        idempotencyKey: newIdempotencyKey(),
      });
      sessionStorage.setItem('subasta_bid_confirmed', '1');
      setConfirmBid(null);
      setToast({ severity: 'success', message: `Oferta enviada · Vas ${data?.value?.puesto || 1}.º` });
      await loadSnapshot(true);
    } catch (err) {
      setToast({ severity: 'error', message: CONFLICT_COPY[err.code] || err.msg || 'No se pudo ofertar' });
      await loadSnapshot(true);
    } finally {
      setSending(false);
    }
  };

  const submitAccept = async () => {
    if (!best?.cod_bid || !snapshot) return;
    setSending(true);
    try {
      const data = await auctionApi.aceptarOferta({
        cod_subasta: Number(cod),
        cod_bid: best.cod_bid,
        version: snapshot.version,
        idempotencyKey: newIdempotencyKey(),
      });
      setAcceptOpen(false);
      const oc = data?.value?.orden_compra;
      setToast({
        severity: 'success',
        message: oc
          ? `Adjudicada. Orden de compra N° ${oc}`
          : 'Adjudicada como cotización. No se creó la orden de compra.',
      });
      await loadSnapshot(true);
    } catch (err) {
      setAcceptOpen(false);
      setToast({ severity: 'error', message: CONFLICT_COPY[err.code] || err.msg || 'No se pudo aceptar' });
      await loadSnapshot(true);
    } finally {
      setSending(false);
    }
  };

  if (!enabled) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">Activa VITE_FF_SUBASTA_LIVE para usar la sala en vivo.</Alert>
      </Box>
    );
  }

  if (loading && !snapshot) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !snapshot) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate(backTo)}>Volver</Button>
      </Box>
    );
  }

  const displayPrice = preview && preview < snapshot.precio_vigente ? preview : snapshot.precio_vigente;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton aria-label="Volver a subastas" onClick={() => navigate(backTo)}>
          <ArrowBack />
        </IconButton>
        <Typography variant="subtitle1" fontWeight={700}>
          Sala subasta N° {snapshot.cod_subasta}
        </Typography>
      </Stack>

      {socketDown && isOpen && (
        <Alert severity="warning" sx={{ mb: 2 }}>Reconectando… actualizando cada 3 s.</Alert>
      )}

      <AuctionLiveStatus status={salaStatus} message={statusMessage} onBack={() => navigate(backTo)} />

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ p: 2, bgcolor: 'background.paper', borderColor: 'divider' }}>
            <Typography variant="subtitle2" color="text.secondary">Precio vigente</Typography>
            <Typography variant="h3" color="primary" aria-live="polite" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
              $ {FormatPrice(displayPrice)}
              <Typography component="span" variant="subtitle1" color="text.secondary">
                {' '}/ {snapshot.producto?.um || ''}
              </Typography>
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
              <AuctionCountdown
                fechaFin={snapshot.fecha_fin_efectiva}
                serverNow={snapshot.server_now}
                clientAt={clientAt}
                onExpire={() => loadSnapshot(true)}
              />
            </Stack>
            <Typography variant="body2" sx={{ mt: 2 }}>
              {snapshot.producto?.cod_erp}-{snapshot.producto?.descripcion}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Cantidad: {FormatPrice(snapshot.cantidad_requerida)} {snapshot.producto?.um}
            </Typography>
            {isBuyer && isOpen && best && (
              <Button fullWidth variant="contained" sx={{ mt: 2 }} onClick={() => setAcceptOpen(true)}>
                Aceptar $ {FormatPrice(best.valor)} · {best.proveedor || 'Proveedor'}
              </Button>
            )}
            {!isBuyer && isOpen && (
              <Box sx={{ mt: 2 }}>
                <QuickBidBar
                  precioVigente={snapshot.precio_vigente}
                  minDecremento={snapshot.min_decremento}
                  disabled={!isOpen}
                  sending={sending}
                  FormatPrice={FormatPrice}
                  onPreview={setPreview}
                  onSubmit={submitBid}
                />
              </Box>
            )}
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ p: 2, bgcolor: 'background.paper', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Ranking {isBuyer ? 'identificado' : 'anónimo'}
            </Typography>
            <AuctionRanking
              ranking={snapshot.ranking}
              variant={isBuyer ? 'buyer' : 'anon'}
              FormatPrice={FormatPrice}
            />
          </Card>
        </Grid>
      </Grid>

      <Dialog open={!!confirmBid} onClose={() => setConfirmBid(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar oferta</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Ofertarás <strong>$ {FormatPrice(confirmBid)}</strong>. No podrás subir este precio.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmBid(null)}>Cancelar</Button>
          <Button variant="contained" onClick={() => sendBid(confirmBid)} disabled={sending}>Ofertar</Button>
        </DialogActions>
      </Dialog>

      <AcceptBidDialog
        open={acceptOpen}
        onClose={() => setAcceptOpen(false)}
        onConfirm={submitAccept}
        sending={sending}
        precio={best?.valor}
        proveedor={best?.proveedor}
        cantidad={snapshot.cantidad_requerida}
        um={snapshot.producto?.um}
        FormatPrice={FormatPrice}
        canCreateOc
      />

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}>
        {toast && <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.message}</Alert>}
      </Snackbar>
    </Box>
  );
};
